/**
 * Cases with a trial date whose expert-designation window is open or already past.
 *
 * An empty list is not "nothing is due" when no trial dates exist on file. The copy below has to
 * say which.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DiscoveryListsApi, {
  ExpertDesignationHeadsUpDto,
  ExpertDesignationHeadsUpRow,
} from '../../api/DiscoveryListsApi';

function dateLabel(value: string): string {
  return value.length >= 10 ? value.slice(0, 10) : value;
}

const ManagerExpertDesignationView: React.FC = () => {
  const navigate = useNavigate();
  const [dto, setDto] = useState<ExpertDesignationHeadsUpDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDto(await DiscoveryListsApi.expertDesignationHeadsUp());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the expert designation list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows: ExpertDesignationHeadsUpRow[] = dto?.headsUps ?? [];
  const datesNeverRecorded = dto != null && dto.casesWithTrialDate === 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Expert designation
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Cases with a trial date on file whose twenty-day expert-designation window is open or already
        past. Raise it at the setting. This page does not file a motion.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : datesNeverRecorded ? (
        <Alert severity="info">
          No trial dates have been recorded ({dto.casesWithTrialDate} of the cases on file). This
          list is empty because the dates were never written down, not because nothing is due.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            As of {dto?.asOf}. {dto?.casesWithTrialDate} case(s) have a trial date on file.
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Citation</TableCell>
                  <TableCell>Court</TableCell>
                  <TableCell>Trial date</TableCell>
                  <TableCell>Designation deadline</TableCell>
                  <TableCell>Window</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No cases are inside the expert-designation window right now.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.ticketGuid}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ticket?Ticket_guid=${row.ticketGuid}`)}
                    >
                      <TableCell>{row.clientName}</TableCell>
                      <TableCell>{row.citationNumber || '—'}</TableCell>
                      <TableCell>{row.courtName || '—'}</TableCell>
                      <TableCell>{dateLabel(row.trialDate)}</TableCell>
                      <TableCell>{dateLabel(row.designationDeadline)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.deadlinePassed ? 'warning' : 'default'}
                          label={
                            row.deadlinePassed
                              ? 'Deadline passed'
                              : `${row.daysUntilDeadline} day(s) to deadline`
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
};

export default ManagerExpertDesignationView;
